import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { CurrencyDollarIcon, PencilIcon, TrashIcon, PlusIcon } from '../utils/icons';

// Mock data for budget categories
const initialCategories = [
  { id: 1, name: 'Groceries', amount: 500, spent: 320 },
  { id: 2, name: 'Dining Out', amount: 200, spent: 180 },
  { id: 3, name: 'Entertainment', amount: 100, spent: 75 },
  { id: 4, name: 'Transportation', amount: 150, spent: 120 },
];

const BudgetManager = ({ activeCategory }) => {
  const { user } = useUser();
  const [categories, setCategories] = useState(initialCategories);
  const [isAdding, setIsAdding] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', amount: '' });
  const [editingId, setEditingId] = useState(null);
  const [editingValues, setEditingValues] = useState({ name: '', amount: '' });

  // Calculate total budget and spent amounts
  const totalBudget = categories.reduce((sum, cat) => sum + cat.amount, 0);
  const totalSpent = categories.reduce((sum, cat) => sum + cat.spent, 0);
  const remaining = totalBudget - totalSpent;
  const progressPercentage = Math.min(100, (totalSpent / totalBudget) * 100) || 0;

  const handleAddCategory = () => {
    if (!newCategory.name || !newCategory.amount) return;
    
    const newId = Math.max(0, ...categories.map(cat => cat.id)) + 1;
    setCategories([
      ...categories,
      {
        id: newId,
        name: newCategory.name,
        amount: Number(newCategory.amount),
        spent: 0
      }
    ]);
    
    setNewCategory({ name: '', amount: '' });
    setIsAdding(false);
  };

  const handleUpdateCategory = (id) => {
    setCategories(categories.map(cat => 
      cat.id === id 
        ? { ...cat, name: editingValues.name, amount: Number(editingValues.amount) }
        : cat
    ));
    setEditingId(null);
  };

  const handleDeleteCategory = (id) => {
    setCategories(categories.filter(cat => cat.id !== id));
  };

  // Filter categories if activeCategory is set
  const displayedCategories = activeCategory
    ? categories.filter(cat => cat.id === activeCategory)
    : categories;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Budget Manager</h2>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-xl">
          <p className="text-sm text-blue-600 font-medium">Total Budget</p>
          <p className="text-2xl font-bold">${totalBudget.toFixed(2)}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-xl">
          <p className="text-sm text-green-600 font-medium">Spent</p>
          <p className="text-2xl font-bold">${totalSpent.toFixed(2)}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-xl">
          <p className="text-sm text-purple-600 font-medium">Remaining</p>
          <p className="text-2xl font-bold">${remaining.toFixed(2)}</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between mb-1">
          <span className="text-sm font-medium text-gray-700">Budget Progress</span>
          <span className="text-sm font-medium text-gray-500">
            ${totalSpent.toFixed(2)} of ${totalBudget.toFixed(2)}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Categories List */}
      <div className="space-y-4 mb-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Categories</h3>
          {!isAdding && (
            <button
              onClick={() => setIsAdding(true)}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PlusIcon className="-ml-1 mr-1 h-4 w-4" />
              Add Category
            </button>
          )}
        </div>

        {isAdding && (
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <label htmlFor="category-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Category Name
                </label>
                <input
                  type="text"
                  id="category-name"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                  placeholder="e.g., Groceries"
                />
              </div>
              <div>
                <label htmlFor="category-amount" className="block text-sm font-medium text-gray-700 mb-1">
                  Budget Amount ($)
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    id="category-amount"
                    className="pl-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    value={newCategory.amount}
                    onChange={(e) => setNewCategory({...newCategory, amount: e.target.value})}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setIsAdding(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCategory}
                className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={!newCategory.name || !newCategory.amount}
              >
                Add Category
              </button>
            </div>
          </div>
        )}

        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                  Category
                </th>
                <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                  Budgeted
                </th>
                <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                  Spent
                </th>
                <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                  Remaining
                </th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {displayedCategories.map((category) => (
                <tr key={category.id}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                    {editingId === category.id ? (
                      <input
                        type="text"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        value={editingValues.name}
                        onChange={(e) => setEditingValues({...editingValues, name: e.target.value})}
                      />
                    ) : (
                      category.name
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500">
                    {editingId === category.id ? (
                      <div className="relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          className="pl-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          value={editingValues.amount}
                          onChange={(e) => setEditingValues({...editingValues, amount: e.target.value})}
                          min="0"
                          step="0.01"
                        />
                      </div>
                    ) : (
                      `$${category.amount.toFixed(2)}`
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500">
                    ${category.spent.toFixed(2)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-right text-sm font-medium">
                    <span className={category.amount - category.spent >= 0 ? 'text-green-600' : 'text-red-600'}>
                      ${(category.amount - category.spent).toFixed(2)}
                    </span>
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    {editingId === category.id ? (
                      <div className="space-x-2">
                        <button
                          onClick={() => handleUpdateCategory(category.id)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-gray-500 hover:text-gray-700 ml-2"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setEditingId(category.id);
                            setEditingValues({
                              name: category.name,
                              amount: category.amount.toString()
                            });
                          }}
                          className="text-gray-400 hover:text-gray-500"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BudgetManager;
